import { useEffect, useState } from 'react';
import { useParams } from 'react-router-dom';
import { useQuery } from '@tanstack/react-query';
import { Markdown } from '@/lib/markdown';
import { getPublicSpace, getPublicPage } from '@/api/wiki';

/**
 * Read-only view of one wiki space, reachable by share token alone. No admin
 * chrome and no links back into the panel — whoever opens this has no account.
 */
export function WikiPublic() {
  const { token = '' } = useParams<{ token: string }>();

  const { data, isLoading, error } = useQuery({
    queryKey: ['wiki-public', token],
    queryFn: () => getPublicSpace(token),
    retry: false,
  });

  const [slug, setSlug] = useState<string | null>(null);
  const pages = data?.pages ?? [];

  useEffect(() => {
    if (pages.length === 0) { setSlug(null); return; }
    if (!slug || !pages.some((p) => p.slug === slug)) setSlug(pages[0]!.slug);
  }, [pages, slug]);

  const { data: page } = useQuery({
    queryKey: ['wiki-public-page', token, slug],
    queryFn: () => getPublicPage(token, slug!),
    enabled: !!slug,
    retry: false,
  });

  // A share link must not end up in a search index even if it leaks.
  useEffect(() => {
    const meta = document.createElement('meta');
    meta.name = 'robots';
    meta.content = 'noindex, nofollow';
    document.head.appendChild(meta);
    return () => { document.head.removeChild(meta); };
  }, []);

  useEffect(() => {
    if (data?.space.name) document.title = data.space.name;
  }, [data?.space.name]);

  if (isLoading) {
    return <Shell><p className="text-sm text-content-subtle">Загрузка…</p></Shell>;
  }

  if (error || !data) {
    return (
      <Shell>
        <h1 className="text-xl font-semibold text-content">Ссылка недействительна</h1>
        <p className="mt-2 text-sm text-content-muted">
          Она была отозвана или никогда не существовала. Запросите новую у того, кто её выдал.
        </p>
      </Shell>
    );
  }

  return (
    <div className="min-h-screen bg-surface-sunken">
      <header className="border-b border-border bg-surface">
        <div className="max-w-5xl mx-auto px-4 py-5">
          <h1 className="text-xl font-semibold text-content">{data.space.name}</h1>
          {data.space.description && (
            <p className="mt-1 text-sm text-content-muted">{data.space.description}</p>
          )}
        </div>
      </header>

      <div className="max-w-5xl mx-auto px-4 py-6 grid grid-cols-1 md:grid-cols-[14rem_1fr] gap-6 items-start">
        <nav className="bg-surface border border-border rounded-lg overflow-hidden">
          <ul className="divide-y divide-border">
            {pages.map((p) => (
              <li key={p.id}>
                <button
                  type="button"
                  onClick={() => setSlug(p.slug)}
                  className={`w-full text-left px-4 py-2 text-sm truncate ${
                    p.slug === slug ? 'bg-surface-muted text-content' : 'text-content-muted hover:text-content'
                  }`}
                  title={p.title}
                >
                  {p.title}
                </button>
              </li>
            ))}
            {pages.length === 0 && (
              <li className="px-4 py-3 text-sm text-content-subtle">Статей пока нет.</li>
            )}
          </ul>
        </nav>

        <article className="min-w-0 bg-surface border border-border rounded-lg px-6 py-5">
          {page ? (
            <>
              <h2 className="text-lg font-semibold text-content">{page.title}</h2>
              <div className="mt-4">
                <Markdown source={page.body_md} />
              </div>
            </>
          ) : (
            <p className="text-sm text-content-subtle">
              {pages.length === 0 ? 'В этом пространстве пока нет статей.' : 'Загрузка…'}
            </p>
          )}
        </article>
      </div>
    </div>
  );
}

function Shell({ children }: { children: React.ReactNode }) {
  return (
    <div className="min-h-screen bg-surface-sunken flex items-center justify-center px-4">
      <div className="max-w-md w-full bg-surface border border-border rounded-lg px-6 py-8 text-center">
        {children}
      </div>
    </div>
  );
}
